import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link2, Loader, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoints';
import { Select } from '../common/Select.js';
import { fetchFlowchartVersions } from '../../request/flowchart/flowchart.api';
import {
  createFlowchartSubprocessLinks,
  deleteFlowchartSubprocessLink,
  fetchFlowchartSubprocessLinks,
} from '../../request/flowchart/subprocessLinks.api';
import { combinedVersionLabel, pickCombinedFlowchartVersion } from './pickCombinedFlowchartVersion';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';

const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 80 }),
  menu: (base) => ({ ...base, zIndex: 80 }),
};

function processCode(process) {
  return String(process?.process_id || process?.id || '').trim();
}

function processUuid(process) {
  return String(process?.id || process?.process_uuid || '').trim();
}

function processName(process) {
  return String(process?.name || processCode(process) || 'Process').trim();
}

function formatNodeLabel(node) {
  const description = String(node?.nodeDescription || node?.node_description || '').trim() || 'Untitled step';
  const actor = String(node?.actorRef?.name || node?.actor || '').trim();
  return actor ? `${description} · ${actor}` : description;
}

function flowchartNodesFromPayload(flowchartData) {
  const nodes = Array.isArray(flowchartData?.nodes) ? flowchartData.nodes : [];
  return nodes
    .filter((node) => {
      if (!node?.id) return false;
      const type = String(
        node?.nodeType || node?.type || node?.data?.nodeType || node?.data?.type || ''
      ).toLowerCase();
      return type !== 'start' && type !== 'end' && type !== 'swimlane';
    })
    .map((node) => ({
      id: String(node.id),
      nodeDescription:
        node.nodeDescription || node.node_description || node.data?.nodeDescription || '',
      nodeType: node.nodeType || node.type || node.data?.nodeType || '',
      actor: node.actorRef?.name || node.actor || node.data?.actorRef?.name || node.data?.actor || '',
    }));
}

async function fetchVersionFlowchart(cycleId, processId, versionNumber) {
  const response = await axiosInstance.get(API_ENDPOINTS.getFlowchart(cycleId, processId), {
    params: { versionNumber },
  });
  if (!response?.data?.success || !response.data?.flowchartData) {
    throw new Error(response?.data?.error || 'Failed to load flowchart');
  }
  return {
    flowchartVersionId: response.data.flowchartVersionId || null,
    versionNumber: response.data.versionNumber ?? versionNumber,
    nodes: flowchartNodesFromPayload(response.data.flowchartData),
  };
}

export default function EndToEndSubprocessLinkModal({
  isOpen,
  cycleId,
  sourceProcess,
  targetProcess,
  onClose,
  onChanged,
}) {
  const [sourceVersions, setSourceVersions] = useState([]);
  const [sourceVersionNumber, setSourceVersionNumber] = useState(null);
  const [sourceFlowchart, setSourceFlowchart] = useState(null);
  const [targetFlowchart, setTargetFlowchart] = useState(null);
  const [targetVersionLabel, setTargetVersionLabel] = useState('');
  const [sourceNodeId, setSourceNodeId] = useState('');
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [existingLinks, setExistingLinks] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState('');

  const sourceCode = processCode(sourceProcess);
  const targetCode = processCode(targetProcess);
  const targetUuid = processUuid(targetProcess);

  const resetState = useCallback(() => {
    setSourceVersions([]);
    setSourceVersionNumber(null);
    setSourceFlowchart(null);
    setTargetFlowchart(null);
    setTargetVersionLabel('');
    setSourceNodeId('');
    setSelectedTargets([]);
    setExistingLinks([]);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      if (!cycleId || !sourceCode || !targetCode) return;
      setInitialLoading(true);
      try {
        const [sourceVersionList, targetVersionList] = await Promise.all([
          fetchFlowchartVersions(cycleId, sourceCode),
          fetchFlowchartVersions(cycleId, targetCode),
        ]);
        if (cancelled) return;
        setSourceVersions(sourceVersionList);

        const targetVersion = pickCombinedFlowchartVersion(targetVersionList);
        if (!targetVersion || targetVersion.value == null) {
          setTargetFlowchart(null);
          setTargetVersionLabel('');
        } else {
          setTargetVersionLabel(combinedVersionLabel(targetVersion));
          const targetChart = await fetchVersionFlowchart(cycleId, targetCode, targetVersion.value);
          if (cancelled) return;
          setTargetFlowchart(targetChart);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.message || 'Failed to load flowchart versions');
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, cycleId, sourceCode, targetCode, resetState]);

  useEffect(() => {
    if (!isOpen || sourceVersionNumber == null || !cycleId || !sourceCode) {
      setSourceFlowchart(null);
      setExistingLinks([]);
      setSourceNodeId('');
      setSelectedTargets([]);
      return undefined;
    }

    let cancelled = false;
    const loadSource = async () => {
      setSourceLoading(true);
      try {
        const chart = await fetchVersionFlowchart(cycleId, sourceCode, sourceVersionNumber);
        if (cancelled) return;
        setSourceFlowchart(chart);
        if (chart.flowchartVersionId) {
          const links = await fetchFlowchartSubprocessLinks(chart.flowchartVersionId);
          if (cancelled) return;
          setExistingLinks(Array.isArray(links) ? links : []);
        } else {
          setExistingLinks([]);
        }
      } catch (err) {
        if (!cancelled) {
          setSourceFlowchart(null);
          setExistingLinks([]);
          toast.error(err?.message || 'Failed to load flowchart nodes');
        }
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    };

    loadSource();
    return () => {
      cancelled = true;
    };
  }, [isOpen, cycleId, sourceCode, sourceVersionNumber]);

  const versionOptions = useMemo(
    () =>
      sourceVersions.map((version) => ({
        value: version.value,
        label: combinedVersionLabel(version) || version.label,
      })),
    [sourceVersions]
  );

  const sourceNodeOptions = useMemo(
    () =>
      (sourceFlowchart?.nodes || []).map((node) => ({
        value: node.id,
        label: formatNodeLabel(node),
      })),
    [sourceFlowchart]
  );

  const linksToTarget = useMemo(
    () =>
      (Array.isArray(existingLinks) ? existingLinks : []).filter(
        (link) => String(link?.target_process_id || '') === targetUuid
      ),
    [existingLinks, targetUuid]
  );

  const connectedTargetNodeIds = useMemo(() => {
    const ids = new Set();
    if (!sourceNodeId) return ids;
    linksToTarget.forEach((link) => {
      if (String(link.source_node_id) !== String(sourceNodeId)) return;
      const nodeId = String(link.target_node_id || '').trim();
      if (nodeId) ids.add(nodeId);
    });
    return ids;
  }, [linksToTarget, sourceNodeId]);

  const targetNodeOptions = useMemo(
    () =>
      (targetFlowchart?.nodes || [])
        .filter((node) => !connectedTargetNodeIds.has(String(node.id)))
        .map((node) => ({
          value: node.id,
          label: formatNodeLabel(node),
          targetProcessId: targetUuid,
          targetNodeId: node.id,
        })),
    [targetFlowchart, targetUuid, connectedTargetNodeIds]
  );

  useEffect(() => {
    setSelectedTargets((prev) => prev.filter((value) => !connectedTargetNodeIds.has(String(value))));
  }, [connectedTargetNodeIds]);

  const handleConnect = useCallback(async () => {
    const flowchartVersionId = sourceFlowchart?.flowchartVersionId;
    if (!flowchartVersionId || !sourceNodeId || selectedTargets.length === 0) {
      toast.error('Select a version, a source node, and at least one target node');
      return;
    }
    setSaving(true);
    try {
      const targets = selectedTargets.map((nodeId) => ({
        targetProcessId: targetUuid,
        targetNodeId: String(nodeId),
      }));
      const result = await createFlowchartSubprocessLinks({
        flowchartVersionId,
        sourceNodeId,
        targets,
      });
      const createdCount = Array.isArray(result?.created_ids) ? result.created_ids.length : 0;
      const skippedCount = Array.isArray(result?.skipped_ids) ? result.skipped_ids.length : 0;
      if (createdCount > 0) {
        toast.success(`Connected ${createdCount} subprocess link${createdCount > 1 ? 's' : ''}`);
      } else if (skippedCount > 0) {
        toast.error('Selected links already exist');
      }
      setSelectedTargets([]);
      const links = await fetchFlowchartSubprocessLinks(flowchartVersionId);
      setExistingLinks(Array.isArray(links) ? links : []);
      onChanged?.();
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, err?.message || 'Failed to connect subprocess links'));
    } finally {
      setSaving(false);
    }
  }, [sourceFlowchart, sourceNodeId, selectedTargets, targetUuid, onChanged]);

  const handleDelete = useCallback(
    async (linkId) => {
      if (!linkId) return;
      setDeletingLinkId(linkId);
      try {
        await deleteFlowchartSubprocessLink(linkId);
        toast.success('Subprocess link removed');
        const flowchartVersionId = sourceFlowchart?.flowchartVersionId;
        if (flowchartVersionId) {
          const links = await fetchFlowchartSubprocessLinks(flowchartVersionId);
          setExistingLinks(Array.isArray(links) ? links : []);
        }
        onChanged?.();
      } catch (err) {
        toast.error(resolve403ErrorMessage(err, err?.message || 'Failed to remove subprocess link'));
      } finally {
        setDeletingLinkId('');
      }
    },
    [sourceFlowchart, onChanged]
  );

  if (!isOpen || !sourceProcess || !targetProcess) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Link processes
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">
              {processName(sourceProcess)}
              <span className="mx-2 text-slate-300">→</span>
              {processName(targetProcess)}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {sourceCode} to {targetCode}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          {initialLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-slate-500">
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Loading flowchart versions...
            </div>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Source flowchart version
                </label>
                <Select
                  value={sourceVersionNumber}
                  onChange={(value) => setSourceVersionNumber(value)}
                  options={versionOptions}
                  isSearchable
                  isClearable
                  placeholder="Select a version..."
                  placeholderOnFocus="Type to search versions..."
                  styles={SELECT_STYLES}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  This process node
                </label>
                <Select
                  value={sourceNodeId}
                  onChange={(value) => setSourceNodeId(value || '')}
                  options={sourceNodeOptions}
                  isSearchable
                  isClearable
                  placeholder={
                    sourceVersionNumber == null
                      ? 'Select a version first...'
                      : sourceLoading
                        ? 'Loading nodes...'
                        : sourceNodeOptions.length === 0
                          ? 'No nodes in this version'
                          : 'Select node on this flowchart...'
                  }
                  placeholderOnFocus="Type to search this process node..."
                  isDisabled={sourceVersionNumber == null || sourceLoading || sourceNodeOptions.length === 0}
                  styles={SELECT_STYLES}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Other process nodes
                </label>
                {targetVersionLabel ? (
                  <p className="mb-1.5 text-[11px] text-slate-500">
                    Using {targetCode} · {targetVersionLabel}
                  </p>
                ) : (
                  <p className="mb-1.5 text-[11px] text-amber-600">
                    No published or draft flowchart found for {targetCode}.
                  </p>
                )}
                <Select
                  isMulti
                  isSearchable
                  value={selectedTargets}
                  onChange={(values) => setSelectedTargets(Array.isArray(values) ? values : [])}
                  options={targetNodeOptions}
                  placeholder={
                    !targetFlowchart
                      ? 'No target nodes available'
                      : targetNodeOptions.length === 0
                        ? 'No other nodes available'
                        : 'Select one or more target nodes...'
                  }
                  placeholderOnFocus="Type to search other process nodes..."
                  isDisabled={
                    sourceVersionNumber == null || !targetFlowchart || targetNodeOptions.length === 0
                  }
                  styles={SELECT_STYLES}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                />
              </div>
              <button
                type="button"
                onClick={handleConnect}
                disabled={saving || sourceLoading || !sourceNodeId || selectedTargets.length === 0}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                <Link2 className="h-4 w-4" />
                {saving ? 'Connecting...' : 'Connect'}
              </button>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Existing links to {targetCode}
                </p>
                {sourceVersionNumber == null ? (
                  <p className="text-sm text-slate-500">Select a source version to see links.</p>
                ) : sourceLoading ? (
                  <p className="flex items-center text-sm text-slate-500">
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Loading links...
                  </p>
                ) : linksToTarget.length === 0 ? (
                  <p className="text-sm text-slate-500">No subprocess links for this version yet.</p>
                ) : (
                  <div className="space-y-2">
                    {linksToTarget.map((link) => {
                      const fromNode = (sourceFlowchart?.nodes || []).find(
                        (node) => String(node.id) === String(link.source_node_id)
                      );
                      return (
                        <div
                          key={link.id}
                          className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3"
                        >
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              From
                            </p>
                            <p className="text-sm font-medium text-slate-900">
                              {formatNodeLabel(fromNode || { nodeDescription: link.source_node_id })}
                            </p>
                            <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              To
                            </p>
                            <p className="text-sm font-medium text-slate-900">
                              {link.target_process_display_id || targetCode} ·{' '}
                              {link.target_node_description || link.target_node_id}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDelete(link.id)}
                            disabled={deletingLinkId === link.id}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
                            aria-label="Remove subprocess link"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
